import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { type TrpcError, useTRPC } from '@/shared/api';

import { openAssetLink, openBlankTab } from '../lib/open-asset-link';
import { startFileDownload } from '../lib/start-file-download';

export interface RequestDownload {
  /**
   * openInNewTab — 링크 자료를 클릭으로 열 때만. 새 탭은 클릭 순간에 열어 둬야 막히지 않는다.
   * 요청 중에 또 부르면 무시한다 — 기록이 두 줄 남고, 빈 탭이 엉뚱한 응답에 쓰인다.
   */
  mutate: (input: { assetId: string; openInNewTab?: boolean }) => void;
  isPending: boolean;
  /** 서버 실패, 또는 받은 링크를 열지 못한 경우(error 는 null) */
  isError: boolean;
  error: TrpcError | null;
}

/**
 * 다운로드 게이트의 서버 호출 (LIB-U-03).
 *
 * 성공 = 서버가 기록을 커밋하고 줄 것을 내준 것 — 파일은 단기 서명 URL 로 즉시 저장을 시작하고,
 * 링크는 그 주소를 연다(클릭에서 열어 둔 탭이 있으면 그 탭, 없으면 같은 탭).
 * UNAUTHORIZED 는 protectedProcedure 가 낸다 — 세션이 없거나(비로그인) 만료돼 재발급까지
 * 실패한 경우다. 화면은 그 코드를 "다시 로그인" 안내로 옮긴다 (fail-closed).
 */
export function useRequestDownload(): RequestDownload {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  /** 클릭 순간 열어 둔 빈 탭 — 응답이 오면 쓰거나 닫는다. 요청마다 새로 정한다. */
  const pendingTab = useRef<Window | null>(null);
  /**
   * 요청 중 표시. mutation.isPending 은 다음 렌더에야 바뀌어, 같은 클릭 안의 두 번째 호출을
   * 막지 못한다 — ref 는 그 자리에서 닫힌다 (use-auto-download 의 isFired 와 같은 이유).
   */
  const isInFlight = useRef(false);
  const [isLinkFailed, setLinkFailed] = useState(false);

  const mutation = useMutation(
    trpc.lib.requestDownload.mutationOptions({
      onSuccess: (result) => {
        // 자료함(lib.myAssets)은 서버 다운로드 기록의 거울이다. 방금 그 기록이 한 줄 늘었는데
        //   캐시(전역 staleTime 60초)를 그대로 두면 /my/downloads 가 한동안 옛 목록을 보여준다.
        void queryClient.invalidateQueries(trpc.lib.myAssets.pathFilter());
        const tab = pendingTab.current;
        pendingTab.current = null;
        if (result.kind === 'link') {
          setLinkFailed(!openAssetLink(result.url, tab));
          return;
        }
        tab?.close();
        startFileDownload(result.url);
      },
      onError: () => {
        pendingTab.current?.close();
        pendingTab.current = null;
      },
      onSettled: () => {
        isInFlight.current = false;
      },
    }),
  );

  const { mutate: send } = mutation;
  const mutate = useCallback<RequestDownload['mutate']>(
    ({ assetId, openInNewTab = false }) => {
      if (isInFlight.current) {
        return;
      }
      isInFlight.current = true;
      setLinkFailed(false);
      pendingTab.current = openInNewTab ? openBlankTab() : null;
      send({ assetId });
    },
    [send],
  );

  return {
    mutate,
    isPending: mutation.isPending,
    isError: mutation.isError || isLinkFailed,
    error: mutation.error,
  };
}
